import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxArchive, faSpinner, faTrash, faCalendarDay, faUserCircle } from '@fortawesome/free-solid-svg-icons';
import { formatDate } from '../../../../utils/dateUtils';
import ArchivedTaskDetailModal from './ArchivedTaskDetailModal';
import { Badge, KebabMenu, PulseDot, SidePanel } from '../../../ui';
import { taskStatusMeta, type ClientTaskRow } from './taskMeta';

interface ArchivedTasksSidepanelProps {
  isOpen: boolean;
  onClose: () => void;
  tasks: ClientTaskRow[];
  isLoading: boolean;
  error: string | null;
  onDelete: (taskId: string) => void;
  onTaskUpdated?: () => void;
}

export default function ArchivedTasksSidepanel({
  isOpen,
  onClose,
  tasks,
  isLoading,
  error,
  onDelete,
  onTaskUpdated,
}: ArchivedTasksSidepanelProps) {
  const [selectedTask, setSelectedTask] = useState<ClientTaskRow | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const handleTaskClick = (task: ClientTaskRow) => {
    setSelectedTask(task);
    setIsDetailModalOpen(true);
  };

  const handleTaskUpdated = () => {
    setIsDetailModalOpen(false);
    setSelectedTask(null);
    if (onTaskUpdated) {
      onTaskUpdated();
    }
  };

  return (
    <>
      <SidePanel open={isOpen} onClose={onClose} width={520} eyebrow="Tasks" title="Archived Tasks">
        <div className="ck-stagger">
          {error && (
            <div
              style={{
                marginBottom: '16px',
                padding: '10px 12px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--status-down-soft)',
                border: '1px solid rgba(229, 72, 77, 0.25)',
                color: 'var(--danger-400)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {error}
            </div>
          )}

          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
              <FontAwesomeIcon
                icon={faSpinner}
                className="animate-spin"
                style={{ color: 'var(--text-faint)', fontSize: '20px' }}
              />
            </div>
          ) : tasks.length === 0 ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
              <FontAwesomeIcon icon={faBoxArchive} style={{ fontSize: '22px', color: 'var(--text-faint)' }} />
              <p style={{ margin: '12px 0 4px', fontSize: 'var(--text-base)' }}>No archived tasks yet</p>
              <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                Archive tasks to keep the workspace tidy while preserving their history.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {tasks.map((task) => {
                const meta = taskStatusMeta(task.status);
                return (
                  <div
                    key={task.id}
                    className="hover-wash"
                    onClick={() => handleTaskClick(task)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '9px 10px',
                      margin: '0 -10px',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                    }}
                  >
                    <PulseDot tone={meta.tone} pulse={false} title={task.status} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          display: 'block',
                          fontSize: 'var(--text-sm)',
                          color: 'var(--text-body)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {task.title}
                      </span>
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          marginTop: '2px',
                          fontSize: 'var(--text-xs)',
                          color: 'var(--text-faint)',
                        }}
                      >
                        {task.archived_at ? (
                          <span className="t-num">Archived {formatDate(task.archived_at)}</span>
                        ) : null}
                        {task.due_date ? (
                          <span className="t-num" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                            <FontAwesomeIcon icon={faCalendarDay} style={{ fontSize: '10px' }} />
                            {formatDate(task.due_date, 'MMM d')}
                          </span>
                        ) : null}
                        {task.assigned_to_name ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                            <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '10px' }} />
                            {task.assigned_to_name}
                          </span>
                        ) : null}
                      </span>
                    </span>
                    <Badge tone={meta.tone}>{task.status}</Badge>
                    <KebabMenu
                      label="Archived task actions"
                      actions={[
                        { label: 'View Task', icon: faBoxArchive, onClick: () => handleTaskClick(task) },
                        { label: 'Delete', icon: faTrash, danger: true, onClick: () => onDelete(task.id) },
                      ]}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </SidePanel>

      {/* Archived Task Detail Modal */}
      <ArchivedTaskDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedTask(null);
        }}
        task={selectedTask}
        onTaskUpdated={handleTaskUpdated}
        onDelete={(taskId) => {
          onDelete(taskId);
          setIsDetailModalOpen(false);
          setSelectedTask(null);
        }}
      />
    </>
  );
}
